import { Alert, Box, Button, Checkbox, CircularProgress, IconButton, Paper, Snackbar, Table, TableBody, TableCell, TableContainer, TableHead, TablePagination, TableRow, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { RequestError, useApi } from "../../services";
import { BarragemData, useUserContext } from "../../context";
import { isAxiosError } from "axios";
import { ErrorModal, SelectBarragem, SelectCompany } from "../modals";
import { Add, Download, OpenInNew } from "@mui/icons-material";
import { Contrato } from "../../services/api/contratos";
import { CompanyData } from "../../pages";
import { NewContratoModal } from "./new_contrato_modal";
import { Barragem } from "../barragem";
import { generateRandomHash } from "../../core";

interface Props {
  title?: string;
  isAddNew?: boolean;
  onSearch?: (id: number) => void;
}
export const TableContratos: React.FC<Props> = ({ title, isAddNew, onSearch }) => {
  const api = useApi();
  const user = useUserContext();

  const [state, setState] = useState(false);
  const [errorAdd, setErrorAdd] = useState<RequestError>();
  const [openNewModal, setOpenNewModal] = useState(false);
  const [contratos, setContratos] = useState<number[] | RequestError>([]);
  const [totalItems, setTotalItems] = useState<number>(0);
  const [perPage, setPerPage] = useState<number>(25);
  const [page, setPage] = useState<number>(1);

  // Lista de Contratos
  useEffect(() => {
    const getContratos = async () => {
      if (!user || "error" in user) return;
      const data = await api.contrato.getAll(page, perPage);
      if ("error" in data) {
        setContratos(data);
        return;
      }
      setTotalItems(data.totalItems);
      setContratos(data.items);
    };
    getContratos();
  }, [page, perPage, user, state]);

  const handleAddNewContrato = async (contrato?: Contrato, file?: File) => {
    if (!contrato || !file) return;

    setOpenNewModal(false);
    await api.contrato.novoContrato(contrato, file).then((value) => {
      if ("error" in value) {
        setErrorAdd(value);
        return;
      }
      setState(!state);
    });
  };

  return <>
    {/* Erro */}
    {errorAdd &&
      <Snackbar
        open={Boolean(errorAdd)}
        autoHideDuration={4500}
        onClose={() => setErrorAdd(undefined)}
        message="Note archived"
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert onClose={() => setErrorAdd(undefined)} severity="error" sx={{ width: "100%" }}>
          {errorAdd.error + ":" + errorAdd.message}
        </Alert>
      </Snackbar>
    }

    {/* Modal para o novo contrato */}
    {isAddNew && openNewModal &&
      <NewContratoModal
        maxWidth="xl"
        open={openNewModal}
        onAdd={handleAddNewContrato}
        onClose={() => setOpenNewModal(false)}
      />
    }

    {/* Tabela com os dados */}
    <Paper sx={{
      display: "flex",
      padding: "15px",
      flexDirection: "column",
    }}>
      {/* Título e novo contrato */}
      <Box sx={{
        display: "flex",
        flexDirection: "row",
      }}>
        <Typography fontWeight={"bold"} fontSize={24}>
          {title ?? "Contratos"}
        </Typography>
        <div style={{ flex: 1 }} />
        {isAddNew && user && !("error" in user) && user.admin &&
          <Button
            variant="contained"
            onClick={() => setOpenNewModal(true)}
            endIcon={<Add />}
          >
            Novo Contrato
          </Button>
        }
      </Box>

      {/* Tabela com os contratos */}
      {"error" in contratos && <ErrorModal {...contratos} />}
      {!("error" in contratos) && <TableContainer>
        <Table aria-label="simple table">
          <TableHead>
            <TableRow>
              {onSearch && <TableCell align="center" sx={{ fontWeight: "bold" }}>Selecionar</TableCell>}
              <TableCell align="center" sx={{ fontWeight: "bold" }}>Contrato/Lote</TableCell>
              <TableCell align="center" sx={{ fontWeight: "bold" }}>Nome</TableCell>
              <TableCell align="center" sx={{ fontWeight: "bold" }}>Empreendimento</TableCell>
              <TableCell align="center" sx={{ fontWeight: "bold" }}>Barragem</TableCell>
              <TableCell align="center" sx={{ fontWeight: "bold" }}>Validade</TableCell>
              <TableCell align="center" sx={{ fontWeight: "bold" }}>Em Vigor</TableCell>
              <TableCell align="center" sx={{ fontWeight: "bold" }}>Download</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {contratos.map(id => <RowWaiting
              key={id}
              id={id}
              onSearch={onSearch}
            />)}
          </TableBody>
        </Table>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50]}
          component="div"
          count={totalItems}
          rowsPerPage={perPage}
          page={page - 1}
          onPageChange={(ev, page) => setPage(page + 1)}
          onRowsPerPageChange={(ev) => setPerPage(parseInt(ev.target.value, 10))}
        />
      </TableContainer>}
    </Paper>
  </>;
};

const RowWaiting: React.FC<{
  id: number;
  onSearch?: (id: number) => void;
}> = ({ id, onSearch }) => {
  const api = useApi();
  const [contrato, setContrato] = useState<Contrato | RequestError>();
  const [company, setCompany] = useState<CompanyData | RequestError>();
  const [barragem, setBarragem] = useState<BarragemData | RequestError>();

  // Dados do contrato
  useEffect(() => {
    api.contrato.getContratoById(id).then(data => setContrato(data));
  }, []);

  // Obtendo os dados da Company
  useEffect(() => {
    if (!contrato || "error" in contrato) return;
    if (!contrato.idEmpreendimento) return;
    api.company.company(contrato.idEmpreendimento).then(data => setCompany(data));
  }, [contrato]);

  // Obtendo os dados da Barragem
  useEffect(() => {
    if (!contrato || "error" in contrato) return;
    if (!contrato.idBarragem) return;
    api.barragem.getBarragemById(contrato.idBarragem).then(data => setBarragem(data));
  }, [contrato]);

  const handleStatus = (status: boolean) => {
    api.contrato.setFinalized(id, status).then((value) => {
      if ("error" in value) return;
      setContrato(value);
    });
  };

  const handleSetCompany = (company?: CompanyData) => {
    if (!contrato || "error" in contrato || !company) return;
    api.contrato.setEmpreendimento(contrato.id, company?.id).then((value) => {
      setContrato(value);
      setTimeout(() => {
        setBarragem(undefined);
      }, 100);
    });
  };

  const handleSetBarragem = (barragem?: BarragemData) => {
    if (!contrato || "error" in contrato || !barragem) return;
    api.contrato.setBarragem(contrato.id, barragem.id).then(setContrato);
  };


  if (!contrato) return <TableRow>
    <TableCell align="center"><CircularProgress size={20} /></TableCell>
    <TableCell align="center"><CircularProgress size={20} /></TableCell>
    <TableCell align="center"><CircularProgress size={20} /></TableCell>
    <TableCell align="center"><CircularProgress size={20} /></TableCell>
    <TableCell align="center"><CircularProgress size={20} /></TableCell>
    <TableCell align="center"><CircularProgress size={20} /></TableCell>
    <TableCell align="center"><CircularProgress size={20} /></TableCell>
    <TableCell align="center"><CircularProgress size={20} /></TableCell>
    <TableCell align="center"><CircularProgress size={20} /></TableCell>
  </TableRow>;

  if ("error" in contrato) return <TableRow>
    <TableCell>Erro</TableCell>
    <TableCell>{contrato.message}</TableCell>
  </TableRow>;

  return <>
    <TableRow>
      {onSearch && <TableCell align="center">
        <IconButton onClick={() => onSearch(id)}>
          <OpenInNew />
        </IconButton>
      </TableCell>}

      {/* Lote e Nome */}
      <TableCell align="center">{contrato.contratoLote}</TableCell>
      <TableCell align="center">{contrato.nome}</TableCell>

      {/* Empreendimento */}
      <TableCell align="center">
        <SelectCompany
          key={generateRandomHash(8)}
          variant="text"
          initialCompany={(company && !("error" in company)) ? company : undefined}
          onSelect={handleSetCompany}
        >
          {company && !("error" in company) ? company.nome : "Selecionar"}
        </SelectCompany>
      </TableCell>

      {/* Barragem */}
      <TableCell align="center">
        <SelectBarragem
          key={generateRandomHash(8)}
          variant="text"
          initialBarragem={(barragem && !("error" in barragem)) ? barragem : undefined}
          onSelect={handleSetBarragem}
        >
          {barragem && !("error" in barragem) ? barragem.nome : "Selecionar"}
        </SelectBarragem>
      </TableCell>

      {/* Validade */}
      <TableCell align="center">{contrato.validade.toLocaleDateString()}</TableCell>

      {/* Status */}
      <TableCell align="center">
        <Checkbox
          checked={contrato.status}
          onChange={(ev, check) => handleStatus(check)}
        />
      </TableCell>

      {/* Download */}
      <TableCell align="center">
        <IconButton onClick={() => {
          window.open(contrato.file, '_blank');
        }}><Download /></IconButton>
      </TableCell>
    </TableRow>
  </>;
};